import { useId, type ReactElement } from 'react';
import { useMeg, useVirksomheter } from '../api/sporringer.js';
import { useValgtVirksomhet, type Omfang } from '../felles/virksomhet.js';

const SKILLE = ':';
const tilVerdi = (o: Omfang): string => `${o.type}${SKILLE}${o.id}`;

/**
 * Omfangsvelgeren i skallet. Én per skjermbilde, ikke én per modul.
 *
 * Kommunalområdet er et likestilt valg, ikke bare en overskrift: en
 * kommunalsjef ser på området, en virksomhetsleder på sitt bygg. Begge står i
 * samme liste, med området over virksomhetene sine, fordi det er den samme
 * avgjørelsen — hva ser jeg på nå.
 *
 * Men området tilbys BARE til den som er tildelt det. Før dette ble «Hele
 * <område>» bygget av områdene som forekom blant virksomhetene dine, og tallet
 * som kom tilbake var summen av de byggene du tilfeldigvis hadde — under en
 * overskrift som lovet hele området. Gruppeoverskriften står fortsatt for alle,
 * fordi den bare forteller hvor virksomheten hører hjemme.
 *
 * Å fjerne valget her er ikke beskyttelsen. Endepunktet svarer 403 uansett
 * hvordan noen kom fram til et områdenummer; dette er bare at menyen slutter å
 * tilby noe som ville blitt avvist.
 */
export function Virksomhetsboks(): ReactElement | null {
  const virksomheter = useVirksomheter();
  const meg = useMeg();
  const { omfang, settOmfang } = useValgtVirksomhet();
  const id = useId();

  const liste = virksomheter.data ?? [];
  // Ingenting å velge mellom er ingen velger. Med én virksomhet er området
  // den samme summen, og en liste med to like tall er bare støy.
  if (liste.length < 2) return null;

  const omrader = [
    ...new Map(liste.map((v) => [v.kommunalomradeId, v.kommunalomradeNavn])).entries(),
  ];

  const tildelteOmrader = new Set((meg.data?.omradetilganger ?? []).map((o) => o.kommunalomradeId));

  return (
    <div className="shrink-0 border-b border-meny-kant px-4 py-3">
      <label htmlFor={id} className="block text-xs font-medium uppercase text-tekst-dempet">
        Viser
      </label>
      <select
        id={id}
        value={omfang === undefined ? '' : tilVerdi(omfang)}
        onChange={(e) => {
          const [type, ...rest] = e.target.value.split(SKILLE);
          settOmfang({ type: type === 'omrade' ? 'omrade' : 'virksomhet', id: rest.join(SKILLE) });
        }}
        className="mt-1 min-h-11 w-full rounded-md border border-kant-sterk bg-flate px-3 text-sm text-tekst lg:min-h-9 lg:text-[0.875rem]"
      >
        {omrader.map(([omradeId, omradeNavn]) => (
          <optgroup key={omradeId} label={omradeNavn}>
            {/* Området først i sin egen gruppe: det er summen av det som står
                under, og rekkefølgen sier det uten en forklaring. Bare for den
                som faktisk er tildelt området. */}
            {tildelteOmrader.has(omradeId) ? (
              <option value={tilVerdi({ type: 'omrade', id: omradeId })}>Hele {omradeNavn}</option>
            ) : null}
            {liste
              .filter((v) => v.kommunalomradeId === omradeId)
              .map((v) => (
                <option key={v.id} value={tilVerdi({ type: 'virksomhet', id: v.id })}>
                  {v.navn}
                </option>
              ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
}
